// Google Analytics 4 Utility
import { IS_DEV } from '@/config/analytics';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

/**
 * Google Analytics 4 Service
 */
export class GoogleAnalytics {
  private static initialized = false;
  private static measurementId: string | null = null;

  /**
   * Initialize Google Analytics with measurement ID
   * Only loads if consent is granted
   */
  static initialize(measurementId: string): void {
    if (this.initialized || !measurementId) {
      return;
    }

    this.measurementId = measurementId;

    // Load gtag script dynamically
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.async = true;
    script.onload = () => {
      this.initGtag(measurementId);
    };
    script.onerror = (error) => {
      console.error('Failed to load Google Analytics script:', error);
    };
    
    document.head.appendChild(script);

    this.initialized = true;

    if (IS_DEV) {
      console.log('Google Analytics initialized with ID:', measurementId);
    }
  }

  /**
   * Initialize gtag function and config
   */
  private static initGtag(measurementId: string): void {
    window.dataLayer = window.dataLayer || [];
    
    // Define gtag function
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };

    // Initialize with privacy-focused settings
    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      page_title: document.title,
      page_location: window.location.href,
      page_path: window.location.pathname,
    });

    if (IS_DEV) {
      console.log('gtag initialized with config:', measurementId);
    }
  }

  /**
   * Track a page view
   */
  static trackPageView(path: string, title: string): void {
    if (!this.isAvailable()) {
      if (IS_DEV) {
        console.log('[Analytics] Page view not tracked (GA not available):', { path, title });
      }
      return;
    }

    window.gtag('event', 'page_view', {
      page_title: title,
      page_location: window.location.origin + path,
      page_path: path,
    });

    if (IS_DEV) {
      console.log('[Analytics] Page view tracked:', { path, title });
    }
  }

  /**
   * Track a custom event
   */
  static trackEvent(
    category: string,
    action: string,
    label?: string,
    value?: number
  ): void {
    if (!this.isAvailable()) {
      if (IS_DEV) {
        console.log('[Analytics] Event not tracked (GA not available):', { category, action, label, value });
      }
      return;
    }

    const eventParams: any = {
      event_category: category,
      event_label: label,
    };

    if (value !== undefined) {
      eventParams.value = value;
    }

    window.gtag('event', action, eventParams);

    if (IS_DEV) {
      console.log('[Analytics] Event tracked:', { category, action, label, value });
    }
  }

  /**
   * Track form submission
   */
  static trackFormSubmission(
    formType: string,
    status: 'started' | 'completed' | 'error' | 'failed',
    errorType?: string
  ): void {
    this.trackEvent('form', `${formType}_${status}`, errorType);
  }

  /**
   * Track payment flow
   */
  static trackPayment(
    paymentType: string,
    action: string,
    amount?: number,
    errorType?: string
  ): void {
    const label = errorType ? `${paymentType}_${errorType}` : paymentType;
    this.trackEvent('payment', action, label, amount);
  }

  /**
   * Track hospital interaction
   */
  static trackHospitalInteraction(
    hospitalId: string,
    action: string,
    interactionType: string
  ): void {
    this.trackEvent('hospital', action, `${hospitalId}_${interactionType}`);
  }

  /**
   * Track service interaction
   */
  static trackServiceInteraction(
    serviceType: string,
    action: string,
    details?: string
  ): void {
    this.trackEvent('service', action, `${serviceType}_${details || ''}`);
  }

  /**
   * Track consent event
   */
  static trackConsentEvent(
    action: string,
    category: string,
    details?: string
  ): void {
    this.trackEvent('consent', action, `${category}_${details || ''}`);
  }

  /**
   * Check if GA is available (initialized and gtag function exists)
   */
  static isAvailable(): boolean {
    const hasGtag = typeof window !== 'undefined' && window.gtag && typeof window.gtag === 'function';
    return this.initialized && hasGtag;
  }

  /**
   * Get current measurement ID
   */
  static getMeasurementId(): string | null {
    return this.measurementId;
  }

  /**
   * Check if GA is initialized
   */
  static isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Reset GA (for testing or when consent is revoked)
   */
  static reset(): void {
    // Remove gtag script if exists
    const script = document.querySelector('script[src*="googletagmanager.com/gtag/js"]');
    if (script) {
      script.remove();
    }

    // Clear dataLayer
    if (window.dataLayer) {
      window.dataLayer.length = 0;
    }

    // Remove gtag function
    (window as any).gtag = undefined;

    this.initialized = false;
    this.measurementId = null;

    if (IS_DEV) {
      console.log('Google Analytics reset');
    }
  }
}

// Convenience functions
export const trackPageView = (path: string, title: string) => 
  GoogleAnalytics.trackPageView(path, title);

export const trackEvent = (category: string, action: string, label?: string, value?: number) =>
  GoogleAnalytics.trackEvent(category, action, label, value);

export const trackFormSubmission = (formType: string, status: 'started' | 'completed' | 'error' | 'failed', errorType?: string) =>
  GoogleAnalytics.trackFormSubmission(formType, status, errorType);

export const trackPayment = (paymentType: string, action: string, amount?: number, errorType?: string) =>
  GoogleAnalytics.trackPayment(paymentType, action, amount, errorType);

export const isAnalyticsAvailable = () => GoogleAnalytics.isAvailable();
export const isAnalyticsInitialized = () => GoogleAnalytics.isInitialized();
export const resetAnalytics = () => GoogleAnalytics.reset();
